/**
 * R232q5i: Sirus-only character rig.
 *
 * Раньше этот loader подгружал Quaternius UBC + UAL animation library +
 * MCOF armor + KayKit weapon meshes. Всё это удалено по требованию юзера
 * («только Sirus»). Сейчас:
 *   - Character GLB грузится из baseDir (обычно /aiwari/models/characters/).
 *     Содержит свой skeleton + animations.
 *   - Armor визуал: только /models/wow-vanilla/{armorClass}/{slot}/{itemId}.gltf
 *     (Sirus extracted) или procedural cape/tabard/waist fallback.
 *   - Weapons: /models/wow-vanilla/weapons/{itemId}.gltf (Sirus extracted).
 *     Если файла нет — slot остаётся пустым.
 *
 * Никаких HEAD-probe в Quaternius/MCOF/KayKit пути. Никаких UBC fallback'ов.
 *
 * R232q5ax: подключены 8160 РЕАЛЬНЫХ Sirus armor/weapon GLB из
 * `/models/sirus/Item/ObjectComponents/`. Manifest лежит рядом
 * (`/models/sirus/manifest.json`) и фетчится один раз (cached). Picker
 * использует race+gender suffix matching (bef/bem/drf/...), детерминирован
 * через `hashStr(itemId)`, и fallback chain полностью сохранён:
 *   exact race+gender → any race → CC0 SIRUS_ARMOR_POOL → procedural.
 */

import {
  type AbstractMesh,
  type AnimationGroup,
  type AssetContainer,
  type Bone,
  type Mesh,
  type Node,
  type Scene,
  type Skeleton,
  Color3,
  MeshBuilder,
  SceneLoader,
  Quaternion,
  StandardMaterial,
  TransformNode,
  Vector3,
} from '@babylonjs/core';
import '@babylonjs/loaders/glTF';
import type { Slot } from '../data/equipment.js';
import { ITEMS } from '../data/equipment.js';
import { sanitizeGlb } from './glb-sanitizer.js';
// R232q51: dedicated weapon-mesh attach module (Sirus item GLB → hand bone,
// master-cached, HEAD-probed). Tried FIRST when a Sirus weapon GLB exists
// for the equipped itemId; existing manifest+CC0 chain remains as fallback.
import { attachWeaponToBone, type AttachableRig } from './ItemMeshAttach.js';
// R232q53: customization mesh-swap (skin/face/hair/facial-hair textures).
// Loader returns a `setCustomization` method that swaps textures on the
// rig sub-meshes; safe to call before, during, or after a slider scrub.
import {
  applyCustomization,
  applyEquipmentArmorOverlays,
  type ArmorOverlaySlot,
  type CharCustomization,
} from './CharCustomization.js';

/**
 * R232q5q+: per-URL master AssetContainer cache. Раньше каждый
 * loadRealisticCharacter call → fresh ImportMeshAsync (~10-71 MB GLB) →
 * Chrome OOM с 30+ trainers + 30+ quest-givers + faction leaders + remote
 * players. Теперь GLB загружается ОДИН раз на URL в виде AssetContainer
 * (не добавлен в сцену, masters не рендерятся), и каждый последующий
 * caller получает свежий instance через instantiateModelsToScene — общая
 * geometry/textures на GPU, per-instance skeleton + animationGroups.
 *
 * Mirror of the GlbMob.ts MODEL_CACHE pattern (KayKitMob.ts использует
 * именно AssetContainer + instantiateModelsToScene — наиболее чистый
 * вариант, поэтому здесь следуем ему).
 */
const MASTER_CACHE = new Map<string, Promise<AssetContainer | null>>();

/**
 * Call this when the Babylon scene is disposed (World.tsx useEffect cleanup).
 * Disposes every cached master AssetContainer so its GPU buffers are freed
 * and the next scene gets fresh masters — иначе instantiateModelsToScene
 * клонировал бы из контейнеров, привязанных к уже disposed сцене.
 */
export function clearRealisticCharacterCache(): void {
  for (const p of MASTER_CACHE.values()) {
    p.then((c) => {
      if (c) {
        try { c.dispose(); } catch { /* already gone */ }
      }
    }).catch(() => {});
  }
  MASTER_CACHE.clear();
}

async function getMasterContainer(scene: Scene, url: string): Promise<AssetContainer | null> {
  let p = MASTER_CACHE.get(url);
  if (p) return p;
  p = (async () => {
    let blobUrl: string | null = null;
    try {
      // R232q5b0: sanitise the GLB JSON chunk in-flight (Sirus M2→GLB
      // extractor emits duplicate skeleton_root with multi-parented bones —
      // glTF spec violation, Babylon rejects with "Invalid recursive node
      // hierarchy"). The sanitiser is a no-op for clean GLBs.
      const sanitised = await sanitizeGlb(url);
      blobUrl = sanitised.blobUrl;
      const { LoadAssetContainerAsync } = await import('@babylonjs/core/Loading/sceneLoader.js');
      // pluginExtension hint — blob URLs don't have a file extension so
      // Babylon can't auto-detect the loader.
      const container = await LoadAssetContainerAsync(blobUrl, scene, { pluginExtension: '.glb' });
      return container;
    } catch (err) {
      const e = err as Error;
      console.error(`[RealisticCharacter] BABYLON PARSE FAILED for ${url}\n  name: ${e?.name}\n  message: ${e?.message}\n  stack: ${e?.stack ?? '(no stack)'}\n`);
      return null;
    } finally {
      // Defer URL revocation: Babylon defers GPU texture upload to the render
      // pipeline AFTER LoadAssetContainerAsync resolves. Revoking the Blob
      // immediately destroys the source before textures are uploaded → red/
      // magenta checkerboard. Give the renderer 3 render frames to finish.
      if (blobUrl) {
        const urlToRevoke = blobUrl;
        let frameCount = 0;
        const obs = scene.onAfterRenderObservable.add(() => {
          frameCount++;
          if (frameCount >= 3) {
            scene.onAfterRenderObservable.remove(obs);
            try { URL.revokeObjectURL(urlToRevoke); } catch { /* nop */ }
          }
        });
        void obs;
      }
    }
  })();
  MASTER_CACHE.set(url, p);
  return p;
}

const AIWARI_CHAR_BASE = `${import.meta.env.BASE_URL}models/characters/`;
const WOW_VANILLA_BASE = `${import.meta.env.BASE_URL}models/wow-vanilla/`;

/** R232q5ax: base path for Sirus item/objectcomponents extracted GLBs.
 *  R232q60: lowercased — Linux nginx is case-sensitive, GLBs are at lowercase paths. */
const SIRUS_ITEM_BASE = `${import.meta.env.BASE_URL}models/sirus/item/objectcomponents/`;
/** R232q5ax: manifest of all 8160 Sirus armor/weapon GLBs grouped by
 *  bucket key (e.g. "Head/plate", "Weapon/sword"). Generated by
 *  `tools/build-sirus-manifest.js` and shipped from `public/models/sirus/`. */
const SIRUS_MANIFEST_URL = `${import.meta.env.BASE_URL}models/sirus/manifest.json`;

/** Race+gender suffix encoded in Sirus filenames (last 3 chars before .glb). */
type SirusRaceSuffix =
  | 'hum' | 'huf' | 'orm' | 'orf' | 'dwm' | 'dwf' | 'nim' | 'nif'
  | 'gnm' | 'gnf' | 'tam' | 'taf' | 'trm' | 'trf' | 'scm' | 'scf'
  | 'bem' | 'bef' | 'drm' | 'drf' | 'gom' | 'gof';

type SirusManifest = Record<string, string[]>;

/** Lazy-fetch + cache the Sirus manifest. Single-flight via in-flight Promise. */
let _sirusManifestPromise: Promise<SirusManifest> | null = null;
async function loadSirusManifest(): Promise<SirusManifest> {
  if (_sirusManifestPromise) return _sirusManifestPromise;
  _sirusManifestPromise = (async () => {
    try {
      const res = await fetch(SIRUS_MANIFEST_URL);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      // nginx SPA fallback returns index.html with 200 for missing files.
      const ct = res.headers.get('content-type') ?? '';
      if (!ct.includes('json')) throw new Error(`manifest missing on CDN (got ${ct})`);
      const json = (await res.json()) as Record<string, unknown>;
      const out: SirusManifest = {};
      for (const [k, v] of Object.entries(json)) {
        if (k.startsWith('_')) continue; // skip _generated / _description / _stats
        if (Array.isArray(v)) out[k] = v as string[];
      }
      return out;
    } catch (e) {
      console.warn('[RealisticCharacter] rig manifest unavailable, falling back to CC0 pool', e);
      return {};
    }
  })();
  return _sirusManifestPromise;
}

/** Map RaceId+gender → 3-char Sirus filename suffix. */
function raceSuffix(raceId: string | null | undefined, gender: 'male' | 'female' | undefined): SirusRaceSuffix | null {
  if (!raceId) return null;
  const g = gender === 'female' ? 'f' : 'm';
  switch (raceId) {
    case 'human':       return (g === 'm' ? 'hum' : 'huf');
    case 'orc':         return (g === 'm' ? 'orm' : 'orf');
    case 'dwarf':       return (g === 'm' ? 'dwm' : 'dwf');
    case 'night-elf':   return (g === 'm' ? 'nim' : 'nif');
    case 'gnome':       return (g === 'm' ? 'gnm' : 'gnf');
    case 'tauren':      return (g === 'm' ? 'tam' : 'taf');
    case 'troll':       return (g === 'm' ? 'trm' : 'trf');
    case 'undead':      return (g === 'm' ? 'scm' : 'scf');
    case 'blood-elf':   return (g === 'm' ? 'bem' : 'bef');
    case 'draenei':     return (g === 'm' ? 'drm' : 'drf');
    case 'goblin':      return (g === 'm' ? 'gom' : 'gof');
    // Outcast races (half-blood, shadowkin) reuse human silhouettes.
    case 'half-blood':  return (g === 'm' ? 'hum' : 'huf');
    case 'shadowkin':   return (g === 'm' ? 'nim' : 'nif');
    default:            return null;
  }
}

export interface RealisticRig {
  root: TransformNode;
  setColours: () => void;
  setMoving: (moving: boolean, running?: boolean) => void;
  update: (dt: number) => void;
  setEquipment: (equipped: Partial<Record<Slot, string>>) => void;
  setCosmetics: (eq: { cape: string | null; pet: string | null }) => void;
  triggerAttack: () => void;
  /** Play named emote animation if rig has it. */
  playEmote: (name: string) => void;
  /** R232q5b1 (Sirus visuals): play a named animation (cast/channel/spell)
   *  once on the rig, returning to idle when the animation ends. The name
   *  is matched case-insensitive against animationGroups + a lenient set
   *  of synonyms so "castFrost" / "spellCast" / "channel" all resolve.
   *  Returns true if a matching anim was found and started. */
  triggerAnim: (name: string) => boolean;
  /**
   * R232q53: swap skin / face / hair / facial-hair textures on this rig
   * to the variants encoded in `custom`. Idempotent — safe to call again
   * (e.g. on every slider change in CharacterCreator). Resolves once
   * every pending texture fetch settles; never throws.
   */
  setCustomization: (custom: CharCustomization) => Promise<void>;
  dispose: () => void;
}

export interface RealisticCharOpts {
  /** Имя GLB файла. */
  glb: string;
  /** Base directory (default: /aiwari/models/characters/). */
  baseDir?: string;
  /** Manual scale multiplier поверх bbox auto-scale (default 1.0). */
  scale?: number;
  /** Target tallest dimension в metres (default 1.8). */
  targetTallest?: number;
  /** R232q5ax: race+gender used to bias armor variant picks toward
   *  matching Sirus race-specific GLBs (e.g. _bem.glb for blood-elf male). */
  raceId?: string | null;
  gender?: 'male' | 'female';
  /** R232q86u: skin tone variant 0-13 (texture suffix NN). */
  skinVariant?: number;
  /** R232q86u: face style 0-12 (texture suffix MM). */
  faceStyle?: number;
}

/** R232q5b0: sentinel filename returned by RaceModelMap for the procedural
 *  placeholder chain step. When the loader sees this it skips the network
 *  fetch entirely and goes straight to the procedural body. */
const PROCEDURAL_PLACEHOLDER_SENTINEL = '__procedural__';

// R232q86za: scene-wide guard. setCustomization re-imports the GLB up to 6
// times (skin/face/hair/eyes/etc.) and each import drops a fresh
// group1679087221_primitive[0..4] secondary-geoset cluster. mat25 ships with
// alpha=0.4 → 6× layered transparent ghost panels = «прозрачная залупа в
// артефактах». Hook scene.onNewMeshAddedObservable ONCE per scene, dispose
// any incoming group mesh + force opaque on its material.
const SECONDARY_GUARD_INSTALLED = new WeakSet<Scene>();
function installSecondaryGeosetGuard(scene: Scene): void {
  if (SECONDARY_GUARD_INSTALLED.has(scene)) return;
  SECONDARY_GUARD_INSTALLED.add(scene);
  scene.onNewMeshAddedObservable.add((mesh) => {
    const n = mesh.name ?? '';
    if (n.startsWith('group1679087221')) {
      try {
        mesh.setEnabled(false);
        mesh.visibility = 0;
        const mat = (mesh as Mesh).material;
        if (mat) {
          mat.alpha = 0;
          if (typeof (mat as { transparencyMode?: number }).transparencyMode === 'number') {
            (mat as { transparencyMode?: number }).transparencyMode = 0;
          }
        }
      } catch { /* nop */ }
    }
  });
}

export async function loadRealisticCharacter(
  scene: Scene,
  opts: RealisticCharOpts,
): Promise<RealisticRig> {
  installSecondaryGeosetGuard(scene);
  const base = opts.baseDir ?? AIWARI_CHAR_BASE;
  const url = `${base}${opts.glb}`;
  // R232q5ax: kick off manifest fetch in parallel (cached after first call).
  void loadSirusManifest();
  const charSuffix = raceSuffix(opts.raceId ?? null, opts.gender);

  // R232q5b0: explicit error handling around Babylon GLB parse. If parse
  // fails (corrupt buffer, unsupported extension, glTF schema mismatch,
  // SPA fallback HTML masquerading as 200) we DO NOT fall back to a
  // cartoon Quaternius rig — we surface the full Babylon error stack and
  // build a neutral "Loading Sirus rig..." procedural body so the world
  // is never empty while the user/devops investigates the upstream GLB.
  type LoadResult = {
    meshes: AbstractMesh[];
    skeletons: Skeleton[];
    animationGroups: AnimationGroup[];
  };
  let result: LoadResult;
  let parseFailed = false;
  if (opts.glb === PROCEDURAL_PLACEHOLDER_SENTINEL) {
    // Sentinel: skip fetch, go straight to procedural body below.
    console.warn(`[RealisticCharacter] placeholder sentinel hit (race chain exhausted). Building procedural "Loading Sirus rig..." body.`);
    result = { meshes: [], skeletons: [], animationGroups: [] };
    parseFailed = true;
  } else {
    // R232q5q+: master AssetContainer cache. First call for this URL:
    // sanitise + LoadAssetContainerAsync → store container (NOT added to
    // scene, masters don't render). Subsequent calls: instantiateModelsToScene
    // gives a fresh per-instance skeleton + animationGroups, sharing
    // geometry/textures on GPU. Avoids Chrome OOM from 200+ identical
    // ~10-71MB GLB re-fetches across NPCs/quest-givers/remote players.
    const container = await getMasterContainer(scene, url);
    if (!container) {
      result = { meshes: [], skeletons: [], animationGroups: [] };
      parseFailed = true;
    } else {
      // Instantiate a fresh copy. nameFunction returns the source name
      // unchanged so downstream regexes (e.g. /^sub_(\d+)_lvl_/i for
      // body-geoset detection) keep matching. cloneMaterials=true so each
      // NPC can have its own tint/visibility refcount without bleeding
      // into other NPCs sharing the same source GLB.
      const inst = container.instantiateModelsToScene((n) => n, true);
      // Walk the instantiated subtree to collect every AbstractMesh —
      // downstream code expects the flat `result.meshes` shape from
      // ImportMeshAsync. Includes the root node when it's a Mesh (has
      // `__root__` name), which the parent-flip below relies on.
      const collected: AbstractMesh[] = [];
      const stack: Node[] = [...inst.rootNodes];
      while (stack.length) {
        const n = stack.pop()!;
        // AbstractMesh subclasses (Mesh, InstancedMesh) have isPickable —
        // pure TransformNode does not. Heuristic: presence of getTotalVertices
        // OR name === '__root__' (which Babylon emits as a Mesh).
        const maybeMesh = n as unknown as { getTotalVertices?: () => number; isPickable?: boolean };
        if (typeof maybeMesh.getTotalVertices === 'function' || n.name === '__root__') {
          collected.push(n as unknown as AbstractMesh);
        }
        for (const ch of n.getChildren()) stack.push(ch);
      }
      result = {
        meshes: collected,
        skeletons: inst.skeletons,
        animationGroups: inst.animationGroups,
      };
      // R232q86o: validation lowered — v1 m2_to_glb.py emits static T-pose
      // GLBs WITHOUT skeleton/animations (это и почему они работают —
      // simple mesh = no multi-parent skeleton issues). «Skeleton missing»
      // больше не фатально. Только если ВООБЩЕ нет mesh данных — fallback.
      const sk = result.skeletons[0];
      const totalVerts = result.meshes.reduce((sum, m) => {
        const mesh = m as { getTotalVertices?: () => number };
        return sum + (mesh.getTotalVertices?.() ?? 0);
      }, 0);
      if (totalVerts < 100) {
        console.error(`[RealisticCharacter] BROKEN GLB: ${url} — no mesh data (totalVerts=${totalVerts}). Falling back to procedural body.`);
        parseFailed = true;
        try { inst.dispose(); } catch { /* nop */ }
        result = { meshes: [], skeletons: [], animationGroups: [] };
      } else if (!sk || (sk.bones?.length ?? 0) < 1) {
        console.info(`[RealisticCharacter] STATIC T-POSE GLB: ${url} — no skeleton (verts=${totalVerts}). Rendering as static mesh.`);
      } else if ((result.animationGroups?.length ?? 0) === 0) {
        console.warn(`[RealisticCharacter] DEGRADED GLB: ${url} — skeleton OK (${sk.bones.length} bones) but 0 animationGroups. Static pose only.`);
      }
    }
  }

  // R232q86zo: keep skeletons + start ONE idle/stand anim per rig. The
  // pywowlib converter names anims as `anim_<id>_<variation>` where id 0 = Stand.
  // The v2 converter named them `stand`, `walk`, `run`. Match both patterns.
  if (result.animationGroups && result.animationGroups.length > 0) {
    const N = result.animationGroups.length;
    let pickIdle: import('@babylonjs/core').AnimationGroup | null = null;
    for (const g of result.animationGroups) {
      const lname = (g.name ?? '').toLowerCase();
      const isIdle =
        lname === 'stand' ||
        lname === 'idle' ||
        lname.startsWith('stand') ||
        lname === 'anim_0_0' ||
        lname.startsWith('anim_0_');
      if (!pickIdle && isIdle) {
        pickIdle = g;
      } else {
        try { g.stop(); } catch { /* nop */ }
      }
    }
    if (pickIdle) {
      try { pickIdle.start(true, 1.0); } catch { /* nop */ }
    } else if (result.animationGroups[0]) {
      try { result.animationGroups[0].start(true, 1.0); } catch { /* nop */ }
    }
    if (N > 50) console.info(`[RealisticCharacter] kept ${N} anim groups, idle=${pickIdle?.name ?? result.animationGroups[0]?.name ?? '(none)'}`);
  }

  // R232q86zbq: LIVE-VERIFIED 2026-05-09 walking direction (2nd iteration).
  // Iteration log:
  //   inner=0       → body walks RIGHT-SHOULDER-FORWARD (sideways)
  //   inner=-π/2    → body walks BACK-FIRST («жопой вперёд», user report)
  //   inner=+π/2    → testing now — should walk face-forward
  // Body's natural-forward is +X in GLB and yaw rotates it in the
  // direction we did not expect. Override via ?facing=N URL param.
  // R232q86zh2 ROOT CAUSE — PROVEN IN THE ISOLATED RIG LAB (scripts/rig-lab.mjs
  // + scratchpad viewer, fixed camera, screenshots on file):
  //   • honest import (scaling.z=-1 kept): model natively faces −X;
  //   • inner = −π/2 → FACES the camera (that was «стоит лицом ко мне»);
  //   • inner = +π/2 + the old z-mirror hack → mirrored body («полубок»);
  //   • inner = +π/2 + π (yesterday) → runs back-first («бежит спиной»);
  //   • inner = +π/2, honest scale → back to camera, runs +Z face-forward,
  //     canonical WoW torso twist into the LEFT shoulder.  ✔ FINAL.
  // Do not «tune» this again — change the exporter instead if it ever changes.
  const facingParam = (() => {
    const def = Math.PI / 2;
    try {
      const p = new URLSearchParams(window.location.search).get('facing');
      const n = p == null ? null : parseFloat(p);
      return Number.isFinite(n) ? (n as number) : def;
    } catch { return def; }
  })();
  const root = new TransformNode(`real-char-root-${opts.glb}`, scene);
  const inner = new TransformNode(`real-char-inner-${opts.glb}`, scene);
  inner.parent = root;
  inner.rotation.y = facingParam;

  // R232q86zi0 PERF: thousands of Geoset meshes dominated the per-frame
  // active-mesh evaluation (18 ms). Cheapest-possible frustum path for every
  // rig mesh: bounding-sphere-only tests and no per-frame bound resync
  // (skinned bounds drift a little — irrelevant for culling accuracy here).
  for (const m of result.meshes) {
    m.cullingStrategy = 3; // BOUNDINGSPHERE_ONLY
    (m as { doNotSyncBoundingInfo?: boolean }).doNotSyncBoundingInfo = true;
  }
  const importRoot = result.meshes.find((m) => m.name === '__root__') ?? result.meshes[0];
  if (importRoot) {
    importRoot.parent = inner;
    // R232q86zh0: the old `scaling.z = 1` reset is GONE. That scaling.z=-1 is
    // Babylon's legitimate RH→LH conversion, NOT an artefact; zeroing it
    // MIRRORED the whole character (left/right swapped, the canonical run
    // torso-twist flipped into the right shoulder — user's «бегу полубоком»).
    // The «walks backwards» symptom it papered over is fixed honestly by the
    // +π in facingParam above.
  }
  for (const m of result.meshes) {
    m.isPickable = false;
    const mesh = m as Mesh;
    if (mesh.material) {
      const mat = mesh.material as {
        backFaceCulling?: boolean;
        transparencyMode?: number;
        // PBR fields (Babylon PBRMaterial)
        albedoTexture?: { name?: string };
        emissiveTexture?: { name?: string } | null;
        emissiveColor?: Color3;
        emissiveIntensity?: number;
        environmentIntensity?: number;
        // StandardMaterial fields
        diffuseTexture?: { name?: string };
      };
      mat.backFaceCulling = true;
      // R232q86h: Sirus M2→GLB exports use Babylon PBR materials by default,
      // which require an HDR environment texture to look right. Without one
      // the character renders almost black (юзер видит «силуэт» вместо
      // нормальной модели). Hack-fix: route the albedo into the emissive
      // slot at 60% intensity so the texture self-illuminates regardless
      // of lighting state. Looks correct under WoW classic flat-lit pipeline.
      const albedoTex = mat.albedoTexture ?? mat.diffuseTexture;
      if (albedoTex && !mat.emissiveTexture) {
        mat.emissiveTexture = albedoTex as { name?: string };
        mat.emissiveColor = new Color3(0.65, 0.65, 0.65);
        if (typeof mat.environmentIntensity === 'number') {
          mat.environmentIntensity = 0.4;
        }
      }
    }
  }

  // R232q86p: bind extracted Sirus BLP→PNG skin texture to v1 GLB meshes.
  // R232q86u/v: Sirus extraction has only one base skin per race (NN=00) but
  // multiple face variants (MM=00..N). So we map:
  //   • opts.faceStyle  → texture MM suffix (real face change in geometry+texture)
  //   • opts.skinVariant → diffuseColor tint (procedural skin tone change,
  //     since darker/tan WoW skin variants weren't extracted as separate PNGs)
  // Texture URL: `<baseFile>skin00_<MM>.png`.
  {
    // R232q86zo: detect Sirus v1 (node0_primitive*), v2 (Mesh_primitive*),
    // AND v3-pywowlib (<RaceFile>Mesh_primitive*, e.g. HumanMaleMesh_primitive0).
    // v3 = proven converter, valid skinning + 146 anims, gltf-validator clean.
    const isV1Mesh = result.meshes.some((m) => m.name?.startsWith('node0_primitive'));
    const isV2Mesh = result.meshes.some((m) => m.name?.startsWith('Mesh_primitive'));
    const isV3Mesh = result.meshes.some((m) => /Mesh_primitive\d+$/i.test(m.name ?? '') && !m.name?.startsWith('Mesh_primitive') && !m.name?.startsWith('node0_primitive'));
    const sirusFamily = isV1Mesh || isV2Mesh || isV3Mesh;
    if (sirusFamily) {
      const race = (opts.raceId ?? 'human').toLowerCase();
      const gender = (opts.gender ?? 'male').toLowerCase();
      const raceFileMap: Record<string, string> = {
        human: 'humanmale', dwarf: 'dwarfmale', 'night-elf': 'nightelfmale',
        gnome: 'gnomemale', orc: 'orcmale', undead: 'scourgemale',
        tauren: 'taurenmale', troll: 'trollmale', goblin: 'goblinmale',
        'half-blood': 'humanmale', shadowkin: 'nightelfmale',
      };
      const baseFile = (raceFileMap[race] ?? 'humanmale').replace('male', gender);
      const skinVariant = (opts.skinVariant ?? 0) % 14;
      const faceStyle = (opts.faceStyle ?? 0) % 13;
      const padMM = String(faceStyle).padStart(2, '0');
      const skinUrl = `${import.meta.env.BASE_URL}data/blp-png/${baseFile}skin00_${padMM}.png`;
      // 14-step procedural tint for skin variants (light → tan → dark).
      // Ordered to match WoW human male skin progression visually.
      const skinTints: Array<[number, number, number]> = [
        [1.00, 1.00, 1.00], [0.95, 0.88, 0.78], [0.90, 0.78, 0.65],
        [0.82, 0.66, 0.52], [0.75, 0.58, 0.42], [0.68, 0.50, 0.34],
        [0.60, 0.43, 0.28], [0.52, 0.36, 0.22], [0.44, 0.30, 0.18],
        [0.95, 0.92, 0.88], [0.85, 0.82, 0.80], [0.70, 0.70, 0.72],
        [0.55, 0.55, 0.58], [0.40, 0.40, 0.42],
      ];
      const tint = skinTints[skinVariant] ?? skinTints[0];
      try {
        const probe = await fetch(skinUrl, { method: 'HEAD' });
        const probeOk = probe.ok && !(probe.headers.get('content-type') ?? '').includes('text/html');
        const fallbackUrl = `${import.meta.env.BASE_URL}data/blp-png/${baseFile}skin00_00.png`;
        const finalUrl = probeOk ? skinUrl : fallbackUrl;
        const { Texture: TextureCls } = await import('@babylonjs/core/Materials/Textures/texture.js');
        const sharedTex = new TextureCls(finalUrl, scene);
        // R232q86z4: CRITICAL — Sirus M2 v1 export emits 61 GEOSETS (NOT 61
        // copies!) sharing one vertex buffer. Each geoset is a triangle SUBSET:
        // geoset 0 = base body (~10000 indices), geoset 60 = tiny eye-glint
        // (12 indices = 4 triangles). Bounding boxes are computed against the
        // shared vertex buffer so they LOOK identical, but actual rendered
        // geometry differs wildly per primitive.
        //
        // Strategy: pick the primitive with the MOST indices (= the base body
        // geoset). Show ONLY that one. Hide all other 60 geosets (decoration /
        // alternate hair / facial-feature overlays whose geosets have no
        // skeletal context to anchor them). This collapses the rig to its
        // single largest mesh and is the correct render path for v1 export.
        const node0Primitives: Mesh[] = [];
        const otherMeshes: Mesh[] = [];
        for (const m of result.meshes) {
          const mesh = m as Mesh;
          if (mesh.name === '__root__') continue;
          if ((mesh.getTotalVertices?.() ?? 0) === 0) continue;
          // R232q86zo: include Sirus body primitives across all 3 export
          // variants — v1 (node0_primitive*), v2 (Mesh_primitive*), v3
          // (HumanMaleMesh_primitive*, OrcMaleMesh_primitive*, etc).
          if (
            mesh.name?.startsWith('node0_primitive') ||
            /Mesh_primitive\d+$/i.test(mesh.name ?? '')
          ) {
            node0Primitives.push(mesh);
          } else {
            otherMeshes.push(mesh);
          }
        }
        // R232q86zd: enable TOP-N largest geosets (body + hair + face + clothes)
        // they all share one vertex buffer — perfect overlay. Top-5 by index
        // count gives the canonical Sirus look (body + hair + face accessory
        // + chest band + cuff). All N share the same StandardMaterial so
        // they read as one cohesive figure with proper texture.
        const ranked = node0Primitives
          .map((m, i) => ({ idx: i, indexCount: m.getIndices?.()?.length ?? 0 }))
          .sort((a, b) => b.indexCount - a.indexCount);
        // R232q86zaa: ENABLE ALL primitives (no MIN threshold). Раньше >300
        // отбрасывало shoulder/face/глаза/hair detail primitives → дыры в
        // плечах, отсутствовали черты лица. Sirus M2 split body на ~60
        // geosets (body + clavicles + shoulders + chest band + cuff +
        // various face features + hair styles). Показываем все ≥30 idx (= 10
        // triangles) — отсекаем только пустышки.
        // R232q86zab: MIN_INDICES=0 для v2/v3 — показать ВСЕ primitives.
        // Юзер: «волосы, лицо, глаза не видны». Все 60+ primitives M2 share
        // правильный IBM от pywowlib_v2 — overlay будет seamless.
        const TOP_N = (isV2Mesh || isV3Mesh) ? 999 : 5;
        const MIN_INDICES = 0;
        const visibleSet = new Set<number>(
          ranked.filter((r) => r.indexCount >= MIN_INDICES).slice(0, TOP_N).map((r) => r.idx),
        );
        if (visibleSet.size === 0 && ranked.length > 0) {
          // Fallback: ensure at least 1 primitive visible.
          visibleSet.add(ranked[0]!.idx);
        }
        const stdMat = new StandardMaterial(`sirus-skin-${baseFile}-s${skinVariant}-f${faceStyle}`, scene);
        stdMat.diffuseTexture = sharedTex;
        stdMat.diffuseColor = new Color3(tint[0], tint[1], tint[2]);
        stdMat.emissiveColor = new Color3(0.08, 0.08, 0.08);
        stdMat.specularColor = new Color3(0, 0, 0);
        stdMat.ambientColor = new Color3(1, 1, 1);
        stdMat.backFaceCulling = true;
        stdMat.alpha = 1.0;
        stdMat.transparencyMode = 0;
        const visibleBody = node0Primitives[ranked[0]!.idx];
        for (let i = 0; i < node0Primitives.length; i++) {
          const mesh = node0Primitives[i]!;
          if (visibleSet.has(i)) {
            mesh.setEnabled(true);
            mesh.visibility = 1;
            // R232q86zaf: per-region COLOR tinting via PBRMaterial.albedoColor
            // (or diffuseColor). User: «бледно-золотой uniform — раскрашенный
            // нужен». Apply visible tints per material name to visually
            // differentiate body/hair/face regions even when underlying
            // textures look similar.
            const existing = mesh.material as {
              diffuseTexture?: unknown; albedoTexture?: unknown;
              name?: string;
              albedoColor?: { r: number; g: number; b: number };
              diffuseColor?: { r: number; g: number; b: number };
              transparencyMode?: number; alpha?: number;
              metallic?: number; roughness?: number;
              emissiveColor?: { r: number; g: number; b: number };
            } | null;
            const hasGlbTex = !!(existing?.diffuseTexture || existing?.albedoTexture);
            if (!hasGlbTex) {
              mesh.material = stdMat;
            } else if (existing) {
              // Force opaque
              if (typeof existing.transparencyMode === 'number') existing.transparencyMode = 0;
              if (typeof existing.alpha === 'number') existing.alpha = 1;
              // R232q86zal: REPLACE PBR material with StandardMaterial of
              // explicit per-region color. PBR.albedoColor multiplies with
              // baseColorTexture — texture content washes out tints. Force
              // distinct colors via StandardMaterial.diffuseColor (no texture
              // for hair/eyeglow, keep texture for body/face).
              const matName = existing.name ?? '';
              const replace = new StandardMaterial(`region-${matName}`, scene);
              replace.specularColor = new Color3(0.05, 0.05, 0.05);
              replace.emissiveColor = new Color3(0, 0, 0);
              replace.ambientColor = new Color3(1, 1, 1);
              replace.alpha = 1.0;
              replace.transparencyMode = 0;
              replace.backFaceCulling = true;
              // R232q86zam: diffuseColor halved (sun intensity 1.10 + hemi
              // 0.65 + rim ~ 1.9 total → clamped to white). Halved values
              // give realistic peach/brown after light multiply.
              // R232q86zas: scene lights reduced (sun 1.10→0.6, hemi 0.65→
              // 0.35, rim 0.15→0.08, total ~1.0× instead of 1.9). Now diffuse
              // can be ~0.7 for proper peach skin tone. Texture restored для
              // body/face details (skin features visible).
              if (/_hair/i.test(matName)) {
                replace.diffuseColor = new Color3(0.40, 0.25, 0.12);
              } else if (/_eyeglow/i.test(matName)) {
                replace.diffuseColor = new Color3(0.25, 0.45, 0.75);
                replace.emissiveColor = new Color3(0.15, 0.28, 0.50);
              } else if (/_face_up/i.test(matName)) {
                replace.diffuseColor = new Color3(0.85, 0.72, 0.65);
                replace.diffuseTexture = sharedTex;
              } else if (/_face/i.test(matName)) {
                replace.diffuseColor = new Color3(0.85, 0.70, 0.62);
                replace.diffuseTexture = sharedTex;
              } else {
                replace.diffuseColor = new Color3(0.78, 0.65, 0.50);
                replace.diffuseTexture = sharedTex;
              }
              mesh.material = replace;
            }
            void isV2Mesh;
          } else {
            mesh.setEnabled(false);
          }
        }
        // R232q86za: HARD-DISABLE secondary group meshes — they survive
        // multi-load (setCustomization re-imports → groups duplicate) and
        // bleed through as transparent artifacts. Walk EVERY descendant of
        // root regardless of whether result.meshes references it. ALSO walk
        // the entire scene for any group1679087221_* mesh that escaped a
        // previous rig dispose. Force opaque material on body + dispose any
        // PBRMaterial / mat25-style alpha on residuals.
        for (const m of otherMeshes) {
          if (m.name?.startsWith('group')) m.setEnabled(false);
        }
        // Walk current root descendants
        for (const child of root.getChildMeshes(false)) {
          if (child.name?.startsWith('group') || child.name?.startsWith('Geoset')) {
            child.setEnabled(false);
          }
        }
        // Walk scene-wide — kill any orphaned group meshes
        for (const m of scene.meshes) {
          if (m.name?.startsWith('group1679087221') && !m.parent) {
            try { m.dispose(); } catch { /* nop */ }
          } else if (m.name?.startsWith('group1679087221')) {
            m.setEnabled(false);
            if (m.material && (m.material.alpha ?? 1) < 1) {
              m.material.alpha = 1;
              m.material.transparencyMode = 0;
            }
          }
        }
        const _topIdx = ranked[0]?.idx ?? -1;
        const _topCount = ranked[0]?.indexCount ?? 0;
        console.info(`[RealisticCharacter] R232q86zbo: top geoset #${_topIdx} (${_topCount} indices), shown=${visibleSet.size}/${node0Primitives.length}, hid ${otherMeshes.filter(m => m.name?.startsWith('group')).length} group overlays. Skin: ${finalUrl.split('/').pop()} tint=(${tint.join(',')})`);
        void visibleBody;
      } catch (e) {
        console.warn(`[RealisticCharacter] skin texture probe failed: ${e}`);
      }
    }
  }

  /* ------------------------------------------------------------------ *
   * R232q5aC — Geoset-aware armor swap (Sprint 2)
   *
   * Sirus character M2 → GLB pipeline (`tmp/m2_to_glb.py`) names every
   * skin sub-mesh `sub_<geosetID>_lvl_<level>`. Per wowdev.wiki/M2/Geosets
   * the geoset ID encodes a *group* in its hundreds digit:
   *   0xx = hair, 1xx = facial features, 2xx = facialhair, 3xx = ears,
   *   4xx = wrist (gauntlet conflict), 5xx = pants/belt overlap,
   *   6xx = boots, 7xx = boots (alt), 8xx = tabard, 9xx = pants,
   *   10xx = cloak, 12xx = hair, 14xx = belt.
   * ItemDisplayInfo.hides_geosets bitmask in the original DB controls per
   * item which groups disappear when equipped. We don't ship that DBC, so
   * we approximate per-slot defaults — good enough for MMORPG silhouette
   * (no more "Knight in plate boots + visible bare toes / hair through
   * helmet"). When DBCs land later, swap SLOT_HIDDEN_GEOSET_GROUPS for a
   * per-itemId map.
   *
   * Enumeration: walk every ImportMesh result mesh whose name starts with
   * `sub_` and parse the geoset ID. Group = floor(id/100). Multi-mesh
   * groups (e.g. several hair geosets) all hide together. Reference-count
   * the hide so two simultaneously-equipped covers don't mis-restore each
   * other (e.g. helm hides hair[0]; full-cover gloves also hide hair[0] —
   * unequipping helm alone must NOT pop hair back).
   * ------------------------------------------------------------------ */
  const SLOT_HIDDEN_GEOSET_GROUPS: Partial<Record<Slot, number[]>> = {
    head:      [0, 1],          // hair + facial features (full helm)
    hands:     [4],             // wrist (gauntlet covers cuff)
    feet:      [5, 6, 7],       // toe / boots base / boots overlay
    chest:     [8, 13],         // tabard chest band + cloak collar overlap
    legs:      [9],             // pants overlap when chausses / robe
    waist:     [13],            // belt (legacy 13xx group)
    cape:      [10],            // cloak default — hide when something else covers it
    shoulders: [],              // shoulders never hide body geosets in WotLK 3.3.5a
    wrists:    [],              // bracers don't hide on Sirus rigs
    shirt:     [],
    tabard:    [],
  };

  /** group → body sub-meshes belonging to that geoset group. */
  const bodyGeosetMeshes = new Map<number, Mesh[]>();
  for (const m of result.meshes) {
    const match = /^sub_(\d+)_lvl_/i.exec(m.name);
    if (!match) continue;
    const id = parseInt(match[1]!, 10);
    if (!Number.isFinite(id)) continue;
    const group = Math.floor(id / 100);
    let bucket = bodyGeosetMeshes.get(group);
    if (!bucket) { bucket = []; bodyGeosetMeshes.set(group, bucket); }
    bucket.push(m as Mesh);
  }

  /** group → hide-refcount. Mesh visible iff refcount === 0. */
  const geosetHideRefcount = new Map<number, number>();
  /** slot → list of groups currently being hidden by the equipped item in that slot (for refcount unwind on unequip). */
  const slotHiddenGroups = new Map<Slot, number[]>();

  function applyGeosetVisibility(): void {
    for (const [group, meshes] of bodyGeosetMeshes) {
      const visible = (geosetHideRefcount.get(group) ?? 0) === 0;
      for (const mesh of meshes) {
        if (mesh.isEnabled() !== visible) mesh.setEnabled(visible);
      }
    }
  }

  function addGeosetHidesForSlot(slot: Slot): void {
    const groups = SLOT_HIDDEN_GEOSET_GROUPS[slot];
    if (!groups || groups.length === 0) return;
    // Idempotent: if slot already had hides applied (re-equip same slot
    // before disposeSlot ran), unwind first to keep refcount honest.
    const prior = slotHiddenGroups.get(slot);
    if (prior) {
      for (const g of prior) {
        const cur = geosetHideRefcount.get(g) ?? 0;
        geosetHideRefcount.set(g, Math.max(0, cur - 1));
      }
    }
    for (const g of groups) {
      geosetHideRefcount.set(g, (geosetHideRefcount.get(g) ?? 0) + 1);
    }
    slotHiddenGroups.set(slot, [...groups]);
    applyGeosetVisibility();
  }

  function removeGeosetHidesForSlot(slot: Slot): void {
    const prior = slotHiddenGroups.get(slot);
    if (!prior || prior.length === 0) return;
    for (const g of prior) {
      const cur = geosetHideRefcount.get(g) ?? 0;
      geosetHideRefcount.set(g, Math.max(0, cur - 1));
    }
    slotHiddenGroups.delete(slot);
    applyGeosetVisibility();
  }

  // bbox auto-normalize — Sirus GLBs могут приходить в разных units.
  let bboxTallest = 0;
  let totalVerts = 0;
  for (const m of result.meshes) {
    if (!(m as Mesh).getTotalVertices || (m as Mesh).getTotalVertices() === 0) continue;
    totalVerts += (m as Mesh).getTotalVertices();
    (m as Mesh).refreshBoundingInfo();
    const bb = (m as Mesh).getBoundingInfo().boundingBox;
    const dy = bb.maximumWorld.y - bb.minimumWorld.y;
    const dx = bb.maximumWorld.x - bb.minimumWorld.x;
    const dz = bb.maximumWorld.z - bb.minimumWorld.z;
    const t = Math.max(dx, dy, dz);
    if (t > bboxTallest) bboxTallest = t;
  }
  // R232q5b0: neutral "Loading Sirus rig..." placeholder.
  // Activates when:
  //   (a) caller passed __procedural__ sentinel (chain exhausted)
  //   (b) Babylon parse threw (corrupt GLB / SPA fallback HTML / unsupported ext)
  //   (c) loaded GLB has no skeleton (BROKEN GLB — see validation above)
  //   (d) imported GLB has zero mesh vertices (skinned mesh refs unresolved)
  //
  // We deliberately do NOT use cartoon Quaternius colours here. The
  // placeholder is uniformly neutral grey so it visually reads as
  // "engineering placeholder, not a finished character". User mandate
  // 2026-05-04: AIWARI must look like Sirus 1:1 WoW — no cartoon fallback.
  const HUMANOID_TINT = '#7A7A7A';   // neutral grey — "Loading Sirus rig..."
  if (parseFailed || totalVerts === 0) {
    if (!parseFailed) {
      console.warn(`[RealisticCharacter] GLB ${opts.glb} loaded with 0 mesh vertices — building "Loading rig..." placeholder body`);
    }
    const procMat = new StandardMaterial(`proc-body-${opts.glb}-mat`, scene);
    procMat.diffuseColor = Color3.FromHexString(HUMANOID_TINT);
    procMat.specularColor = new Color3(0.08, 0.08, 0.08);
    const procMatHead = new StandardMaterial(`proc-head-${opts.glb}-mat`, scene);
    procMatHead.diffuseColor = Color3.FromHexString('#999999');   // neutral grey head
    procMatHead.specularColor = new Color3(0.05, 0.05, 0.05);
    // Body capsule (height 1.2 m).
    const body = MeshBuilder.CreateCapsule(`proc-body-${opts.glb}`, { height: 1.2, radius: 0.32, tessellation: 12 }, scene);
    body.material = procMat;
    body.parent = inner;
    body.position.y = 0.7;
    body.isPickable = false;
    // Head sphere.
    const head = MeshBuilder.CreateSphere(`proc-head-${opts.glb}`, { diameter: 0.36, segments: 12 }, scene);
    head.material = procMatHead;
    head.parent = inner;
    head.position.y = 1.55;
    head.isPickable = false;
    // Arms — two cylinders.
    for (const sign of [-1, 1]) {
      const arm = MeshBuilder.CreateCapsule(`proc-arm-${sign === -1 ? 'L' : 'R'}-${opts.glb}`, { height: 0.7, radius: 0.10, tessellation: 8 }, scene);
      arm.material = procMat;
      arm.parent = inner;
      arm.position.x = sign * 0.36;
      arm.position.y = 0.95;
      arm.isPickable = false;
    }
    // Legs — two cylinders.
    for (const sign of [-1, 1]) {
      const leg = MeshBuilder.CreateCapsule(`proc-leg-${sign === -1 ? 'L' : 'R'}-${opts.glb}`, { height: 0.85, radius: 0.13, tessellation: 8 }, scene);
      leg.material = procMat;
      leg.parent = inner;
      leg.position.x = sign * 0.16;
      leg.position.y = 0.0;
      leg.isPickable = false;
    }
    // Engineering-marker eyes — small dim red dots so it reads as
    // "placeholder, not finished". User mandate: NO cartoon glow.
    const eyeMat = new StandardMaterial(`proc-eye-${opts.glb}-mat`, scene);
    eyeMat.diffuseColor = Color3.FromHexString('#552222');
    eyeMat.emissiveColor = Color3.FromHexString('#552222');
    eyeMat.specularColor = new Color3(0, 0, 0);
    for (const sign of [-1, 1]) {
      const eye = MeshBuilder.CreateSphere(`proc-eye-${sign === -1 ? 'L' : 'R'}-${opts.glb}`, { diameter: 0.05 }, scene);
      eye.material = eyeMat;
      eye.parent = inner;
      eye.position.x = sign * 0.06;
      eye.position.y = 1.58;
      eye.position.z = -0.16;
      eye.isPickable = false;
    }
    // Treat procedural body as the bbox: ~1.85m tall.
    bboxTallest = 1.85;
  }
  const target = opts.targetTallest ?? 1.8;
  const autoScale = (bboxTallest > 0.001) ? (target / bboxTallest) : 1.0;
  const userScale = opts.scale ?? 1.0;
  const finalScale = autoScale * userScale;
  root.scaling = new Vector3(finalScale, finalScale, finalScale);

  // R232q5i: animations теперь живут только внутри embedded GLB. Никаких
  // UAL retarget'ов. Если Sirus rig пришёл без анимаций — character просто
  // стоит статикой (RealisticRig.update procedural-bob fallback закроет дыру).
  const skeleton: Skeleton | null = result.skeletons[0] ?? null;
  const animGroups: AnimationGroup[] = [...(result.animationGroups || [])];
  let idleGroup: AnimationGroup | null = null;
  let walkGroup: AnimationGroup | null = null;
  let runGroup: AnimationGroup | null = null;
  for (const g of animGroups) g.stop();
  const exact = (name: string): AnimationGroup | null =>
    animGroups.find((g) => g.name.toLowerCase() === name.toLowerCase()) ?? null;
  // R232q86zp: pywowlib emits anims as `anim_<id>_<variation>` where M2 ids are
  // 0=Stand, 4=Walk, 5=Run. Match these FIRST, fall back to KayKit naming.
  const animById = (id: number): AnimationGroup | null =>
    animGroups.find((g) => new RegExp(`^anim_${id}(_|$)`, 'i').test(g.name)) ?? null;
  idleGroup = animById(0)
    ?? exact('Idle_Loop')
    ?? exact('Idle')
    ?? exact('Idle_A')
    ?? animGroups.find((g) => /^idle/i.test(g.name) && !/sit|sleep|crouch|sword|spell|pistol|swim|driving/i.test(g.name)) ?? null;
  walkGroup = animById(4)
    ?? exact('Walk_Loop')
    ?? exact('Walk')
    ?? exact('Walk_A')
    ?? animGroups.find((g) => /^walk/i.test(g.name) && !/back|formal|swim|crouch/i.test(g.name)) ?? null;
  runGroup = animById(5)
    ?? exact('Jog_Fwd_Loop')
    ?? exact('Sprint_Loop')
    ?? exact('Run')
    ?? exact('Run_A')
    ?? animGroups.find((g) => /jog|sprint|run/i.test(g.name) && !/swim|crouch/i.test(g.name)) ?? null;
  if (!idleGroup) {
    idleGroup = animGroups.find((g) => !/death|attack|hit|die|hurt|kill|swing|cast|sit|sleep|crouch|swim|driving/i.test(g.name)) ?? animGroups[0] ?? null;
  }
  if (!walkGroup && runGroup) walkGroup = runGroup;
  if (!runGroup && walkGroup) runGroup = walkGroup;
  if (idleGroup) idleGroup.start(true);

  let current: AnimationGroup | null = idleGroup;
  let bobClock = 0;
  let isMovingNow = false;

  function findBone(patterns: RegExp[]): Bone | null {
    if (!skeleton) return null;
    for (const p of patterns) {
      const b = skeleton.bones.find((bn) => p.test(bn.name));
      if (b) return b;
    }
    return null;
  }
  const handR = findBone([/^hand_r$/i, /right_?hand/i, /Bip01.*R.*Hand/i, /mixamorig:RightHand/i, /handslot\.r/i, /Fist\.R/i]);
  const handL = findBone([/^hand_l$/i, /left_?hand/i, /Bip01.*L.*Hand/i, /mixamorig:LeftHand/i, /handslot\.l/i, /Fist\.L/i]);

  // R232q5ap: per-slot bone resolution for armor attach. Quaternius UACP
  // rigs ship single-mesh humanoids with BAKED-IN clothing (Knight = full
  // plate), so armor GLBs must visibly sit ON TOP of the base body, parented
  // to the right bone, normalised to a believable bbox. armor GLBs from
  // poly.pizza are static (no skin) — if we let .skeleton be carried over
  // they snap to bind pose. Removed the skeleton assignment in attachArmor.
  const torsoBone   = findBone([/^torso$/i, /^chest$/i, /spine_?2|upperback/i, /^spine$/i]);
  const hipBone     = findBone([/^hips$/i, /^hip$/i, /pelvis|root_?hip/i]);
  const headBone    = findBone([/^head$/i, /mixamorig:Head/i, /Bip01.*Head/i]);
  const shoulderL   = findBone([/^shoulder\.l$/i, /left_?shoulder/i, /upperarm.*l$/i, /clavicle.*l$/i]);
  const shoulderR   = findBone([/^shoulder\.r$/i, /right_?shoulder/i, /upperarm.*r$/i, /clavicle.*r$/i]);
  const footL       = findBone([/^foot\.l$/i, /left_?foot/i, /toe.*l$/i]);
  const footR       = findBone([/^foot\.r$/i, /right_?foot/i, /toe.*r$/i]);
  const lowerArmL   = findBone([/^lowerarm\.l$/i, /forearm.*l$/i, /^foreArm_l$/i]);
  const lowerArmR   = findBone([/^lowerarm\.r$/i, /forearm.*r$/i, /^foreArm_r$/i]);
  const upperLegL   = findBone([/^upperleg\.l$/i, /left_?upperleg/i, /thigh.*l$/i]);
  const upperLegR   = findBone([/^upperleg\.r$/i, /right_?upperleg/i, /thigh.*r$/i]);

  /** R232q5ap: per-slot armor placement. Resolves the bone to attach to,
   *  the local-space offset, the rotation and the desired bbox tallest
   *  dimension (so we can normalise piece-of-armor to body proportions
   *  regardless of source GLB scale). */
  interface ArmorPlacement {
    bone: Bone | null;
    offset: Vector3;
    rotEuler: Vector3;
    targetSize: number; // metres — tallest bbox dim after normalisation
  }
  function placementFor(slot: Slot): ArmorPlacement {
    switch (slot) {
      case 'chest':     return { bone: torsoBone, offset: new Vector3(0, 0, 0),     rotEuler: new Vector3(0, 0, 0), targetSize: 0.55 };
      case 'shoulders': return { bone: torsoBone, offset: new Vector3(0, 0.10, 0),  rotEuler: new Vector3(0, 0, 0), targetSize: 0.65 };
      case 'head':      return { bone: headBone,  offset: new Vector3(0, 0.05, 0),  rotEuler: new Vector3(0, 0, 0), targetSize: 0.30 };
      case 'legs':      return { bone: hipBone,   offset: new Vector3(0, -0.10, 0), rotEuler: new Vector3(0, 0, 0), targetSize: 0.55 };
      case 'feet':      return { bone: footL,     offset: new Vector3(0, 0, 0),     rotEuler: new Vector3(0, 0, 0), targetSize: 0.18 };
      case 'hands':     return { bone: lowerArmL, offset: new Vector3(0, 0, 0),     rotEuler: new Vector3(0, 0, 0), targetSize: 0.20 };
      case 'wrists':    return { bone: lowerArmR, offset: new Vector3(0, 0, 0),     rotEuler: new Vector3(0, 0, 0), targetSize: 0.16 };
      case 'waist':     return { bone: hipBone,   offset: new Vector3(0, 0.05, 0),  rotEuler: new Vector3(0, 0, 0), targetSize: 0.40 };
      default:          return { bone: torsoBone, offset: new Vector3(0, 0, 0),     rotEuler: new Vector3(0, 0, 0), targetSize: 0.50 };
    }
  }
  // Reserved bones (R/L mirror attachments — to be wired when separable
  // shoulder/foot pairs land in the asset pool):
  void shoulderL; void shoulderR; void footR; void upperLegL; void upperLegR;

  const attachedMeshes = new Map<Slot, AbstractMesh[]>();
  const attachedRoots = new Map<Slot, TransformNode | null>();
  function disposeSlot(slot: Slot): void {
    const prev = attachedMeshes.get(slot);
    if (prev) {
      for (const m of prev) (m as Mesh).dispose(false, true);
      attachedMeshes.delete(slot);
    }
    const r = attachedRoots.get(slot);
    if (r) r.dispose(false, true);
    attachedRoots.delete(slot);
    // R232q5aC: restore body geosets that this slot was hiding.
    removeGeosetHidesForSlot(slot);
  }

  /**
   * Procedural cape/tabard/waist generator. Используется когда Sirus
   * GLB для itemId ещё не извлечён — даём хотя бы видимый visual.
   */
  function buildProceduralCloth(slot: Slot, item: { id: string; tint?: string; name?: { ru?: string } }): TransformNode | null {
    const tintHex = (item as { tint?: string }).tint;
    const colour = (tintHex && /^#[0-9a-f]{6}$/i.test(tintHex))
      ? Color3.FromHexString(tintHex)
      : Color3.FromHexString('#6b3b1a');
    const mat = new StandardMaterial(`proc-${slot}-${item.id}-mat`, scene);
    mat.diffuseColor = colour;
    mat.specularColor = new Color3(0.05, 0.05, 0.05);
    mat.backFaceCulling = false;
    const procRoot = new TransformNode(`proc-${slot}-${item.id}`, scene);
    if (slot === 'cape') {
      const cape = MeshBuilder.CreateGround(`${procRoot.name}-mesh`, { width: 0.55, height: 0.95, subdivisions: 2 }, scene);
      cape.material = mat;
      cape.parent = procRoot;
      cape.rotation.x = Math.PI / 2;
      cape.position.y = -0.45;
      cape.position.z = 0.06;
      const spine = findBone([/spine_?2|chest|upperback/i, /^spine$/i]);
      const tn = spine?.getTransformNode();
      if (tn) procRoot.parent = tn;
      else procRoot.parent = inner;
    } else if (slot === 'tabard') {
      const front = MeshBuilder.CreateGround(`${procRoot.name}-front`, { width: 0.30, height: 0.70 }, scene);
      const back  = MeshBuilder.CreateGround(`${procRoot.name}-back`,  { width: 0.30, height: 0.70 }, scene);
      front.material = mat; back.material = mat;
      front.parent = procRoot; back.parent = procRoot;
      front.rotation.x = Math.PI / 2; front.position.z = 0.10;
      back.rotation.x  = Math.PI / 2; back.position.z = -0.10;
      front.position.y = -0.30; back.position.y = -0.30;
      const spine = findBone([/spine|chest/i, /^spine$/i]);
      const tn = spine?.getTransformNode();
      if (tn) procRoot.parent = tn;
      else procRoot.parent = inner;
    } else if (slot === 'waist') {
      const sash = MeshBuilder.CreateCylinder(`${procRoot.name}-sash`, { diameter: 0.40, height: 0.10, tessellation: 16 }, scene);
      sash.material = mat;
      sash.parent = procRoot;
      const hip = findBone([/hip|pelvis|root/i]);
      const tn = hip?.getTransformNode();
      if (tn) procRoot.parent = tn;
      else procRoot.parent = inner;
    } else {
      return null;
    }
    for (const m of procRoot.getChildMeshes(false)) m.isPickable = false;
    return procRoot;
  }

  /** R232q5ax: deterministic hash → integer for picking a stable GLB
   *  variant. Defined early so it's available to the manifest pickers below. */
  function hashStr(s: string): number {
    let h = 0;
    for (let i = 0; i < s.length; i++) h = ((h << 5) - h + s.charCodeAt(i)) | 0;
    return Math.abs(h);
  }

  /** R232q5ax: map (armor slot, armor class) → Sirus manifest bucket key.
   *  Returns null when the slot is not represented in the extracted dump
   *  (Sirus 3.3.5 ships only Head + Shoulder for armor; Chest/Legs/etc.
   *  are baked into the body M2 — modular swap there requires geoset
   *  recombination outside the scope of this commit). */
  function sirusArmorBucket(slot: Slot, armorClass: string | undefined): string | null {
    if (!armorClass) return null;
    const cls = armorClass === 'cloth' ? 'cloth' : armorClass; // direct map
    switch (slot) {
      case 'head':      return `Head/${cls}`;
      case 'shoulders': return `Shoulder/${cls}`;
      default:          return null;
    }
  }

  /** Map weapon item id → Sirus manifest bucket key. Mirrors
   *  `genericWeaponFile` heuristics but returns the manifest key, not a file. */
  function sirusWeaponBucket(itemId: string, slot: Slot): string | null {
    if (slot === 'offhand' && /shield|buckler|targe/i.test(itemId)) return 'Shield/all';
    if (/crossbow/i.test(itemId))                                   return 'Weapon/crossbow';
    if (/firearm|gun|pistol|rifle|musket/i.test(itemId))            return 'Weapon/firearm';
    if (/glaive|warglaive/i.test(itemId))                           return 'Weapon/glaive';
    if (/polearm|halberd|spear|lance|naginata/i.test(itemId))       return 'Weapon/polearm';
    if (/wand/i.test(itemId))                                       return 'Weapon/wand';
    if (/staff|scepter|sceptre|stave/i.test(itemId))                return 'Weapon/staff';
    if (/bow/i.test(itemId))                                        return 'Weapon/bow';
    if (/dagger|knife|shiv|stiletto|dirk/i.test(itemId))            return 'Weapon/dagger';
    if (/throwing|throwable|thrown|chakram/i.test(itemId))          return 'Weapon/thrown';
    if (/fist|claw|knuckle|gauntlet-weapon|katar/i.test(itemId))    return 'Weapon/fist';
    if (/hammer|maul/i.test(itemId))                                return 'Weapon/hammer';
    if (/axe|cleaver/i.test(itemId))                                return 'Weapon/axe';
    if (/mace|club|morningstar|flail/i.test(itemId))                return 'Weapon/mace';
    if (/orb|trinket|tome|book/i.test(itemId))                      return 'Weapon/misc';
    if (/sword|blade|sabre|saber|rapier|katana|cutlass|scimitar|falchion/i.test(itemId)) return 'Weapon/sword';
    return 'Weapon/sword';
  }

  /** Map a manifest bucket key to its on-disk subdirectory. */
  function bucketSubdir(bucket: string): string {
    // bucket form is "<Subdir>/<Class>" — first segment is the subdir.
    const slash = bucket.indexOf('/');
    return slash > 0 ? bucket.slice(0, slash) : bucket;
  }

  /** R232q5ax: pick a real Sirus GLB from the manifest with race-suffix
   *  preference. Returns the absolute URL or null when bucket is empty.
   *  Fallback chain inside this picker:
   *    1. files matching `_<suffix>.glb` for player race+gender
   *    2. ANY file in the bucket (race-agnostic — race-less weapon GLBs
   *       always land here)
   *  Deterministic via hashStr(itemId). */
  function pickFromSirusBucket(
    manifest: SirusManifest,
    bucket: string | null,
    suffix: SirusRaceSuffix | null,
    itemId: string,
  ): string | null {
    if (!bucket) return null;
    const pool = manifest[bucket];
    if (!pool || pool.length === 0) return null;
    const subdir = bucketSubdir(bucket);
    const seed = hashStr(itemId);
    if (suffix) {
      const tail = `_${suffix}.glb`;
      const matched = pool.filter((f) => f.toLowerCase().endsWith(tail));
      if (matched.length > 0) {
        const file = matched[seed % matched.length] ?? matched[0]!;
        return `${SIRUS_ITEM_BASE}${subdir}/${file}`;
      }
    }
    // No race suffix or no match — pick any.
    const file = pool[seed % pool.length] ?? pool[0]!;
    return `${SIRUS_ITEM_BASE}${subdir}/${file}`;
  }

  /** R232q5x: maps {armorClass, slot} → pool of GLBs (Sirus-extracted + CC0).
   *  Picker is deterministic via hashStr(itemId) — same item always renders
   *  with same mesh. Returns the picked URL. */
  const SIRUS_ARMOR_POOL: Record<string, string[]> = {
    // Until Sirus M2 armor is extracted, use the CC0 poly.pizza pool.
    // Re-populate this from MPQ extraction in R232q5y+.
    'cloth/chest':     ['robe-1.glb', 'robe-2.glb', 'robe-3.glb', 'robe-mage.glb', 'robe-priest.glb', 'robe-wizard.glb', 'tunic.glb'],
    'cloth/legs':      ['robe-1.glb', 'robe-2.glb', 'robe-3.glb'],
    'leather/chest':   ['armor-leather.glb', 'armor-used.glb', 'fighter-leather.glb'],
    'leather/feet':    ['fighter-leather.glb'],
    'mail/chest':      ['armor-mail.glb', 'arrow-knight.glb', 'chestplate-mail-1.glb', 'chestplate-mail-2.glb', 'chestplate-mail-3.glb'],
    'plate/chest':     ['armor-golden.glb', 'armor-metal.glb', 'chestplate-plate-1.glb', 'chestplate-plate-2.glb', 'chestplate-plate-3.glb'],
    'plate/head':      ['helmet-1.glb', 'helmet-2.glb', 'helmet-3.glb', 'helmet-4.glb', 'helmet-5.glb'],
    'plate/shoulders': ['pauldron-1.glb', 'pauldron-2.glb', 'pauldron-3.glb'],
    'plate/hands':     ['pauldron-1.glb'],
  };

  function genericArmorUrl(armorClass: string, slot: string, itemId: string): string | null {
    const pool = SIRUS_ARMOR_POOL[`${armorClass}/${slot}`];
    if (!pool || pool.length === 0) return null;
    const file = pool[hashStr(itemId) % pool.length] ?? pool[0]!;
    return `${WOW_VANILLA_BASE}${armorClass}/${slot}/${file}`;
  }

  /** Загружает armor mesh. Path probe sequence:
   *    1. /models/wow-vanilla/{armorClass}/{slot}/{itemId}.gltf  (Sirus M2 extraction)
   *    2. /models/wow-vanilla/{armorClass}/{slot}/{itemId}.glb   (named CC0 file)
   *    3. /models/wow-vanilla/{armorClass}/{slot}/<generic>.glb  (poly.pizza fallback)
   *    4. procedural cape/tabard/waist if applicable
   *    5. empty slot
   */
  async function attachArmor(slot: Slot, itemId: string): Promise<void> {
    disposeSlot(slot);
    if (!itemId) return;
    const item = ITEMS.find((it) => it.id === itemId);
    if (!item) return;
    const armorClass = (item as { armorClass?: string }).armorClass;
    const supportedSlots: Slot[] = ['chest', 'legs', 'feet', 'hands', 'wrists', 'waist', 'shoulders', 'head'];
    if (armorClass && supportedSlots.includes(slot)) {
      const candidates: string[] = [
        `${WOW_VANILLA_BASE}${armorClass}/${slot}/${itemId}.gltf`,
        `${WOW_VANILLA_BASE}${armorClass}/${slot}/${itemId}.glb`,
      ];
      // R232q5ax: Sirus manifest pick (race-suffix preferred). Inserted
      // BEFORE the CC0 generic so real extracted assets win when available.
      try {
        const manifest = await loadSirusManifest();
        const bucket = sirusArmorBucket(slot, armorClass);
        const sirusUrl = pickFromSirusBucket(manifest, bucket, charSuffix, itemId);
        if (sirusUrl) candidates.push(sirusUrl);
      } catch { /* manifest unreachable — proceed with CC0 generic */ }
      const generic = genericArmorUrl(armorClass, slot, itemId);
      if (generic) candidates.push(generic);
      for (const wowUrl of candidates) {
        try {
          const res = await fetch(wowUrl, { method: 'HEAD' });
          if (!res.ok) continue;
          const loaded = await SceneLoader.ImportMeshAsync('', '', wowUrl, scene);
          const validMeshes = loaded.meshes.filter((m) => (m as Mesh).getTotalVertices && (m as Mesh).getTotalVertices() > 0) as Mesh[];
          if (validMeshes.length === 0) continue;

          // R232q5ap: armor GLBs (poly.pizza-style props) ship without
          // skinning and at wildly varying source scale (helmet bbox ≈10m,
          // chestplate ≈1m, etc). Quaternius race rigs are single-mesh
          // humanoids with BAKED-IN clothing/armor so the armor must visibly
          // sit on top via a per-bone TransformNode + uniform bbox-normalise.
          //
          // Pipeline:
          //  1. compute combined bbox of valid meshes (in their own local frame).
          //  2. derive uniform scale = targetSize / tallest-bbox-dim.
          //  3. build attachRoot, parent it to the resolved bone (or inner
          //     fallback) and apply offset/rot.
          //  4. parent every mesh under attachRoot, clear any inherited
          //     transform on the import-root node so we start from origin.
          //  5. NEVER assign rig.skeleton — these meshes are static; doing
          //     so snaps them to bind pose and they vanish/teleport.
          const placement = placementFor(slot);

          let armorTallest = 0;
          for (const m of validMeshes) {
            m.refreshBoundingInfo();
            const bb = m.getBoundingInfo().boundingBox;
            // Use local-frame extents (extendSize * 2) so we don't pick up
            // pre-existing inherited scale.
            const ex = bb.extendSize;
            const dx = ex.x * 2, dy = ex.y * 2, dz = ex.z * 2;
            const t = Math.max(dx, dy, dz);
            if (t > armorTallest) armorTallest = t;
          }
          const uniformScale = (armorTallest > 0.001) ? (placement.targetSize / armorTallest) : 1.0;

          const attachRoot = new TransformNode(`armor-${slot}-${itemId}`, scene);
          const boneTn = placement.bone?.getTransformNode();
          if (boneTn) {
            attachRoot.parent = boneTn;
          } else {
            // Fallback: parent to inner with body-relative offset so the
            // piece is still visible even on rigs without named bones.
            attachRoot.parent = inner;
            const fallbackY: Record<string, number> = {
              chest: 1.20, shoulders: 1.40, head: 1.55, legs: 0.55,
              feet: 0.05, hands: 0.95, wrists: 0.95, waist: 0.95,
            };
            attachRoot.position.y = fallbackY[slot] ?? 1.0;
          }
          attachRoot.position.addInPlace(placement.offset);
          attachRoot.rotation = placement.rotEuler.clone();
          attachRoot.scaling = new Vector3(uniformScale, uniformScale, uniformScale);

          // Re-parent every loaded mesh (including __root__ if present) to
          // attachRoot, clearing imported transforms so we start clean.
          const importRoot = loaded.meshes.find((m) => m.name === '__root__') as TransformNode | undefined;
          if (importRoot) {
            importRoot.parent = attachRoot;
            importRoot.position = Vector3.Zero();
            importRoot.scaling = new Vector3(1, 1, 1);
            if (importRoot.rotationQuaternion) importRoot.rotationQuaternion = Quaternion.Identity();
            else importRoot.rotation = Vector3.Zero();
          }
          for (const m of loaded.meshes) {
            if (m === importRoot) continue;
            // R232q5ap: do NOT bind rig skeleton onto static armor mesh.
            // Quaternius rig has a non-matching topology — binding causes
            // the static mesh to deform to bind-pose at origin (invisible).
            if (!importRoot && m.parent !== attachRoot) {
              m.parent = attachRoot;
            }
            m.isPickable = false;
          }
          // Dispose any skeletons the armor GLB shipped with — we render
          // it as static geometry attached to the rig bone.
          for (const sk of loaded.skeletons) sk.dispose();

          attachedMeshes.set(slot, loaded.meshes);
          attachedRoots.set(slot, attachRoot);
          // R232q5aC: hide body geosets occluded by this armor piece.
          addGeosetHidesForSlot(slot);
          return;
        } catch { /* try next candidate */ }
      }
    }
    // Procedural fallback для cape/tabard/waist (нет GLB on disk).
    if (slot === 'cape' || slot === 'tabard' || slot === 'waist') {
      const proc = buildProceduralCloth(slot, item as { id: string; tint?: string });
      if (proc) {
        attachedRoots.set(slot, proc);
        attachedMeshes.set(slot, proc.getChildMeshes(false));
        addGeosetHidesForSlot(slot);
      }
    }
  }

  /** R232q5x: Sirus-extracted weapon GLB pools per category (80 OBJs → GLBs
   *  in `weapons/all/`). Seeded by Babylon-friendly OBJ→GLB conversion via
   *  obj2gltf. Each category lists actual files on disk. CC0 poly.pizza
   *  files act as fallbacks at the end of each pool. Picker is deterministic
   *  via hashStr(itemId) so the same item always renders with same mesh. */
  const SIRUS_WEAPON_POOL: Record<string, string[]> = {
    sword: [
      'Sword_1H_Northrend_C_03.glb', 'Sword_1H_PVP320_C_01.glb', 'Sword_1H_Stratholme_D_01.glb',
      'Sword_1H_Cataclysm_C_01.glb', 'Sword_1H_OutlandRaid_D_01.glb', 'Sword_1H_Pandaria_A_01.glb',
      'Sword_1H_AhnQiraj_D_02.glb', 'Sword_2H_Northrend_C_02.glb', 'Sword_2H_PVP400_D_01.glb',
      'Sword_2H_Stratholme_D_01.glb', 'Sword_2H_Cataclysm_C_01.glb', 'Sword_2H_AhnQiraj_D_02.glb',
      'Sword_2H_OutlandRaid_D_01.glb', 'Sword_2H_Pandaria_A_01.glb',
      'long-sword.glb', 'sword-fantasy.glb', 'longsword.glb', 'short-sword.glb', 'hoplite-sword.glb',
    ],
    axe: [
      'Axe_1H_AllianceCovenant_D_01.glb', 'Axe_1H_Horde_D_03.glb', 'Axe_1H_Northrend_B_01.glb',
      'Axe_2H_Deathknight_C_01.glb', 'Axe_2H_Horde_A_01.glb', 'Axe_2H_Horde_D_01.glb',
      'Axe_2H_Northrend_C_02.glb', 'Axe_2H_ZulAman_D_01.glb',
      'axe-1.glb', 'axe-battle.glb', 'axe-double.glb', 'axe-hand.glb',
    ],
    mace: [
      'Mace_1H_Northrend_C_01.glb', 'Mace_1H_PVP320_C_01.glb', 'Mace_1H_Stratholme_D_01.glb',
      'Mace_1H_Cataclysm_C_01.glb', 'Mace_2H_Northrend_C_01.glb', 'Mace_2H_PVP400_D_01.glb',
      'Mace_2H_Cataclysm_C_01.glb', 'Mace_2H_AhnQiraj_D_02.glb', 'Mace_2H_OutlandRaid_D_01.glb',
      'Mace_2H_Pandaria_A_01.glb',
      'mace-1.glb', 'mace-2.glb', 'mace-3.glb', 'mace-d20.glb',
    ],
    hammer: [
      'Hammer_2H_Northrend_C_01.glb', 'Hammer_2H_OutlandRaid_D_01.glb',
      'hammer-1.glb', 'hammer-war.glb',
    ],
    staff: [
      'Stave_2H_Northrend_C_01.glb', 'Stave_2H_PVP400_D_01.glb', 'Stave_2H_Stratholme_D_01.glb',
      'Stave_2H_Cataclysm_C_01.glb', 'Stave_2H_AhnQiraj_D_02.glb', 'Stave_2H_OutlandRaid_D_01.glb',
      'Stave_2H_Pandaria_A_01.glb', 'Stave_2H_Naxxramas_D_01.glb', 'Stave_2H_PVP320_C_01.glb',
      'Stave_2H_ZulAman_D_01.glb',
      'staff-1.glb', 'staff-2.glb', 'staff-mage.glb', 'staff-wizard.glb',
    ],
    wand: ['Wand_1H_Northrend_C_01.glb'],
    bow: [
      'Bow_1H_Advanced_C_01.glb', 'Bow_1H_Advanced_D_01.glb', 'Bow_1H_AhnQiraj_D_02.glb',
      'Bow_1H_Nexus_D_02.glb', 'Bow_1H_OutlandRaid_D_01.glb', 'Bow_1H_PVP320_C_01.glb',
      'Bow_1H_Stratholme_D_01.glb',
      'bow-1.glb', 'bow-2.glb', 'bow-recurve.glb',
    ],
    crossbow: [
      'Bow_2H_Crossbow_Draenei_A_02.glb', 'Bow_2H_Crossbow_Northrend_B_01.glb',
      'Bow_2H_Crossbow_OutlandPVP_D_01.glb',
    ],
    dagger: [
      'Knife_1H_Northrend_C_01.glb', 'Knife_1H_PVP320_C_01.glb', 'Knife_1H_Stratholme_D_01.glb',
      'Knife_1H_Cataclysm_C_01.glb', 'Knife_1H_AhnQiraj_D_02.glb', 'Knife_1H_OutlandRaid_D_01.glb',
      'dagger-quaternius.glb',
    ],
    polearm: [
      'Polearm_2H_Northrend_C_01.glb', 'Polearm_2H_OutlandRaid_D_01.glb',
      'Polearm_2H_AhnQiraj_D_02.glb', 'Polearm_2H_Pandaria_A_01.glb',
      'spear-1.glb', 'spear-2.glb',
    ],
    glaive: ['Glave_1H_Outland_D_01.glb'],
    fist: [
      'Hand_1H_Northrend_C_01.glb', 'Hand_1H_OutlandRaid_D_01.glb', 'Hand_1H_AhnQiraj_D_02.glb',
    ],
    firearm: [
      'Firearm_2H_Cataclysm_C_01.glb', 'Firearm_2H_Northrend_C_01.glb',
    ],
    thrown: [
      'Thrown_1H_Northrend_C_01.glb', 'Thrown_1H_OutlandRaid_D_01.glb',
    ],
    shield: [
      'Offhand_1H_Northrend_C_01.glb',
      'shield-1.glb', 'shield-2.glb', 'shield-celtic.glb', 'shield-round.glb',
    ],
    misc: ['Misc_1H_Northrend_C_01.glb', 'Misc_1H_OutlandRaid_D_01.glb', 'Misc_1H_AhnQiraj_D_02.glb'],
  };

  /** Picks a Sirus-extracted weapon GLB (or CC0 fallback) by item category.
   *  Deterministic per-itemId so persistence is stable. */
  function genericWeaponFile(itemId: string, slot: Slot): string | null {
    let cat: keyof typeof SIRUS_WEAPON_POOL = 'sword';
    if (slot === 'offhand' && /shield|buckler|targe/i.test(itemId))   cat = 'shield';
    else if (/crossbow/i.test(itemId))                                cat = 'crossbow';
    else if (/firearm|gun|pistol|rifle|musket/i.test(itemId))         cat = 'firearm';
    else if (/glaive|warglaive/i.test(itemId))                        cat = 'glaive';
    else if (/polearm|halberd|spear|lance|naginata/i.test(itemId))    cat = 'polearm';
    else if (/wand/i.test(itemId))                                    cat = 'wand';
    else if (/staff|scepter|sceptre/i.test(itemId))                   cat = 'staff';
    else if (/bow/i.test(itemId))                                     cat = 'bow';
    else if (/dagger|knife|shiv|stiletto|dirk/i.test(itemId))         cat = 'dagger';
    else if (/throwing|throwable|thrown|chakram/i.test(itemId))       cat = 'thrown';
    else if (/fist|claw|knuckle|gauntlet-weapon|katar/i.test(itemId)) cat = 'fist';
    else if (/hammer|maul/i.test(itemId))                             cat = 'hammer';
    else if (/axe|cleaver/i.test(itemId))                             cat = 'axe';
    else if (/mace|club|morningstar|flail/i.test(itemId))             cat = 'mace';
    else if (/sword|blade|sabre|saber|rapier|katana|cutlass|scimitar|falchion/i.test(itemId)) cat = 'sword';
    else if (/orb|trinket|tome|book/i.test(itemId))                   cat = 'misc';
    else                                                              cat = 'sword';
    const pool = SIRUS_WEAPON_POOL[cat];
    if (!pool || pool.length === 0) return 'long-sword.glb';
    return pool[hashStr(itemId) % pool.length] ?? pool[0]!;
  }

  /** Загружает weapon mesh. Path probe sequence:
   *    1. /models/wow-vanilla/weapons/{itemId}.gltf   (Sirus M2)
   *    2. /models/wow-vanilla/weapons/{itemId}.glb    (named CC0)
   *    3. /models/wow-vanilla/weapons/all/{itemId}.glb (CC0 named)
   *    4. /models/wow-vanilla/weapons/all/<generic>.glb (poly.pizza fallback)
   *    5. procedural box-blade + handle */
  async function attachWeapon(slot: Slot, itemId: string): Promise<void> {
    disposeSlot(slot);
    if (!itemId) return;
    const item = ITEMS.find((it) => it.id === itemId);
    if (!item) return;
    const bone = (slot === 'mainhand') ? handR : (slot === 'offhand') ? handL : null;
    // R232q86w: skeletonless v1 rigs have no handR/handL bones — но визуально
    // оружие должно появляться. ItemMeshAttach имеет встроенный fallback (pin
    // to inner с offset). Раньше тут был ранний return при !bone — теперь
    // проваливаемся дальше, и весь chain (ItemMeshAttach + legacy candidates +
    // procedural) использует inner как fallback parent.

    // R232q51: try the dedicated ItemMeshAttach module FIRST. It probes
    // /aiwari/models/sirus/Item/ObjectComponents/weapon/<itemId>.glb (HEAD-
    // probed, master-cached) and falls back to a generic CC0 sword. If it
    // returns a TransformNode, register it for next-call disposeSlot and
    // exit before the legacy manifest+CC0 chain runs. If it returns null
    // (no Sirus weapon GLB + generic also missing), fall through silently.
    if (slot === 'mainhand' || slot === 'offhand') {
      const rigShim: AttachableRig = { root, skeleton, inner };
      try {
        const node = await attachWeaponToBone(rigShim, slot, itemId);
        if (node) {
          attachedRoots.set(slot, node);
          return;
        }
      } catch { /* swallow — fall through to legacy chain */ }
    }

    const generic = genericWeaponFile(itemId, slot);
    const candidates: string[] = [
      `${WOW_VANILLA_BASE}weapons/${itemId}.gltf`,
      `${WOW_VANILLA_BASE}weapons/${itemId}.glb`,
      `${WOW_VANILLA_BASE}weapons/all/${itemId}.glb`,
    ];
    // R232q5ax: Sirus manifest pick. Weapons have no race suffix in
    // Sirus naming, so we pass `null` for charSuffix even on player rigs.
    try {
      const manifest = await loadSirusManifest();
      const bucket = sirusWeaponBucket(itemId, slot);
      const sirusUrl = pickFromSirusBucket(manifest, bucket, null, itemId);
      if (sirusUrl) candidates.push(sirusUrl);
    } catch { /* manifest unreachable — proceed with CC0 generic */ }
    if (generic) candidates.push(`${WOW_VANILLA_BASE}weapons/all/${generic}`);

    for (const wowUrl of candidates) {
      try {
        const probe = await fetch(wowUrl, { method: 'HEAD' });
        if (!probe.ok) continue;
        const loaded = await SceneLoader.ImportMeshAsync('', '', wowUrl, scene);
        const meshes = loaded.meshes.filter((m) => (m as Mesh).getTotalVertices && (m as Mesh).getTotalVertices() > 0) as Mesh[];
        if (meshes.length === 0) continue;
        const tn = bone?.getTransformNode();
        const attachRoot = new TransformNode(`weapon-${slot}-${item.id}`, scene);
        if (tn) {
          attachRoot.parent = tn;
          attachRoot.position = new Vector3(0, 0, 0);
        } else {
          // Skeletonless v1 fallback: pin to inner at hand-side offset so the
          // weapon is visible on the side of the body.
          attachRoot.parent = inner;
          const xOff = (slot === 'mainhand') ? 0.30 : -0.30;
          attachRoot.position = new Vector3(xOff, 1.05, 0.10);
        }
        attachRoot.rotationQuaternion = Quaternion.RotationYawPitchRoll(0, Math.PI / 2, 0);
        attachRoot.scaling = new Vector3(0.7, 0.7, 0.7);
        for (const m of meshes) {
          m.parent = attachRoot;
          m.isPickable = false;
        }
        attachedMeshes.set(slot, loaded.meshes);
        attachedRoots.set(slot, attachRoot);
        return;
      } catch { /* try next candidate */ }
    }
    // Procedural fallback — никакой URL не сработал.
    buildProceduralWeapon(slot, item, bone);
  }

  /** Procedural weapon: blade box + handle cylinder + pommel sphere, coloured
   *  by item rarity. Attached to the hand bone via TransformNode (or to
   *  `inner` with hand-side offset on skeletonless rigs). */
  function buildProceduralWeapon(slot: Slot, item: { id: string; name?: { ru?: string }; rarity?: string }, bone: Bone | null): void {
    const tn = bone?.getTransformNode();
    const root = new TransformNode(`proc-weapon-${slot}-${item.id}`, scene);
    if (tn) {
      root.parent = tn;
      root.position = new Vector3(0, 0, 0);
    } else {
      root.parent = inner;
      const xOff = (slot === 'mainhand') ? 0.30 : -0.30;
      root.position = new Vector3(xOff, 1.05, 0.10);
    }
    root.rotationQuaternion = Quaternion.RotationYawPitchRoll(0, Math.PI / 2, 0);
    // Rarity colour ramp.
    const RARITY_COL: Record<string, string> = {
      common: '#9C8E72', uncommon: '#5BAE57', rare: '#3E6FA5', epic: '#6B3F8C', legendary: '#E8C77B',
    };
    const colour = RARITY_COL[item.rarity ?? 'common'] ?? '#9C8E72';
    // Detect weapon kind from item id.
    const isAxe = /axe|hammer|mace/i.test(item.id);
    const isStaff = /staff|wand|scepter|sceptre/i.test(item.id);
    const isBow = /bow|crossbow/i.test(item.id);
    const isDagger = /dagger|knife|shiv|throwing/i.test(item.id);
    const isShield = slot === 'offhand' && /shield|buckler|targe/i.test(item.id);
    const bladeMat = new StandardMaterial(`proc-weapon-${item.id}-mat`, scene);
    bladeMat.diffuseColor = Color3.FromHexString(isStaff || isBow ? '#5C4520' : '#C0C8D8');
    bladeMat.specularColor = new Color3(0.4, 0.4, 0.4);
    const accentMat = new StandardMaterial(`proc-weapon-${item.id}-acc`, scene);
    accentMat.diffuseColor = Color3.FromHexString(colour);
    accentMat.specularColor = new Color3(0.2, 0.2, 0.2);
    if (isShield) {
      const shield = MeshBuilder.CreateBox(`${root.name}-mesh`, { width: 0.06, height: 0.45, depth: 0.42 }, scene);
      shield.material = accentMat;
      shield.parent = root;
      shield.position = new Vector3(0.05, 0, 0.0);
      shield.isPickable = false;
    } else if (isStaff) {
      const shaft = MeshBuilder.CreateCylinder(`${root.name}-shaft`, { height: 1.6, diameter: 0.04 }, scene);
      shaft.material = bladeMat;
      shaft.parent = root;
      shaft.rotation.z = Math.PI / 2;
      shaft.position = new Vector3(0.6, 0, 0);
      shaft.isPickable = false;
      const orb = MeshBuilder.CreateSphere(`${root.name}-orb`, { diameter: 0.10 }, scene);
      orb.material = accentMat;
      orb.parent = root;
      orb.position = new Vector3(1.4, 0, 0);
      orb.isPickable = false;
    } else if (isBow) {
      const bow = MeshBuilder.CreateTorus(`${root.name}-bow`, { diameter: 0.7, thickness: 0.025, tessellation: 12 }, scene);
      bow.material = bladeMat;
      bow.parent = root;
      bow.rotation.x = Math.PI / 2;
      bow.position = new Vector3(0.35, 0, 0);
      bow.isPickable = false;
    } else if (isDagger) {
      const blade = MeshBuilder.CreateBox(`${root.name}-blade`, { width: 0.30, height: 0.018, depth: 0.05 }, scene);
      blade.material = bladeMat;
      blade.parent = root;
      blade.position = new Vector3(0.20, 0, 0);
      blade.isPickable = false;
      const grip = MeshBuilder.CreateCylinder(`${root.name}-grip`, { height: 0.12, diameter: 0.025 }, scene);
      grip.material = accentMat;
      grip.parent = root;
      grip.rotation.z = Math.PI / 2;
      grip.position = new Vector3(0, 0, 0);
      grip.isPickable = false;
    } else if (isAxe) {
      const handle = MeshBuilder.CreateCylinder(`${root.name}-handle`, { height: 0.85, diameter: 0.035 }, scene);
      handle.material = bladeMat;
      handle.parent = root;
      handle.rotation.z = Math.PI / 2;
      handle.position = new Vector3(0.42, 0, 0);
      handle.isPickable = false;
      const head = MeshBuilder.CreateBox(`${root.name}-head`, { width: 0.16, height: 0.20, depth: 0.05 }, scene);
      head.material = accentMat;
      head.parent = root;
      head.position = new Vector3(0.78, 0.06, 0);
      head.isPickable = false;
    } else {
      // Default sword.
      const blade = MeshBuilder.CreateBox(`${root.name}-blade`, { width: 0.7, height: 0.022, depth: 0.07 }, scene);
      blade.material = bladeMat;
      blade.parent = root;
      blade.position = new Vector3(0.5, 0, 0);
      blade.isPickable = false;
      const guard = MeshBuilder.CreateBox(`${root.name}-guard`, { width: 0.05, height: 0.025, depth: 0.18 }, scene);
      guard.material = accentMat;
      guard.parent = root;
      guard.position = new Vector3(0.10, 0, 0);
      guard.isPickable = false;
      const grip = MeshBuilder.CreateCylinder(`${root.name}-grip`, { height: 0.18, diameter: 0.030 }, scene);
      grip.material = accentMat;
      grip.parent = root;
      grip.rotation.z = Math.PI / 2;
      grip.position = new Vector3(0, 0, 0);
      grip.isPickable = false;
      const pommel = MeshBuilder.CreateSphere(`${root.name}-pommel`, { diameter: 0.05 }, scene);
      pommel.material = accentMat;
      pommel.parent = root;
      pommel.position = new Vector3(-0.10, 0, 0);
      pommel.isPickable = false;
    }
    attachedRoots.set(slot, root);
  }

  function play(g: AnimationGroup | null, loop = true): void {
    if (!g || g === current) return;
    if (current) current.stop();
    g.start(loop);
    current = g;
  }

  return {
    root,
    setColours: () => { /* Sirus rigs имеют baked textures — diffuse override не нужен */ },
    setMoving: (moving, running) => {
      isMovingNow = moving;
      // R232q86zap: re-enabled real walk anim — World.tsx targetYaw + 3π/2
      // (instead of +π) аligns mesh face AND walk anim feet direction with
      // motion direction +Z. Total rotation π flips bind face -Z → +Z и
      // foot step -Z → +Z, both match motion.
      if (moving && running && runGroup) play(runGroup);
      else if (moving && walkGroup) play(walkGroup);
      else if (idleGroup) play(idleGroup);
    },
    update: (dt: number) => {
      // R232q86r: МНОГО более заметная procedural animation. Юзер сообщил
      // «фигурка ползает без движения» при amplitude 1.5cm. Теперь:
      // Idle breathe — sway side-to-side ±5° + gentle bob 4cm + slight rotation
      // Walking — strong step bob ±10cm + roll ±10° + forward lean.
      // Без skeleton (v1 GLB) bone-level motion невозможна, но whole-body
      // motion дает visible пластику.
      // R232q86z8: amplified procedural «пластика». Юзер: «без движений ползает».
      // Without skeleton we can only animate the whole inner node — but this
      // gives idle-breathe + walk-bob + scan-yaw that reads as «alive».
      const innerNode = root.getChildren()[0] as TransformNode | undefined;
      if (!innerNode) return;
      bobClock += dt;
      // R232q86zag: baseY = инициальная inner.rotation.y (URL param controllable).
      const baseY = inner.rotation.y;
      if (isMovingNow) {
        innerNode.position.y = -0.05 + Math.sin(bobClock * 7) * 0.04;
        innerNode.rotation.z = Math.sin(bobClock * 3.5) * 0.10;
        innerNode.rotation.x = -0.04 + Math.sin(bobClock * 7) * 0.04;
        innerNode.rotation.y = baseY;
      } else {
        innerNode.position.y = -0.05 + Math.sin(bobClock * 1.6) * 0.02;
        innerNode.rotation.z = Math.sin(bobClock * 0.7) * 0.04;
        innerNode.rotation.x = Math.sin(bobClock * 0.4) * 0.02;
        innerNode.rotation.y = baseY;
      }
    },
    setEquipment: (equipped) => {
      void attachWeapon('mainhand', equipped.mainhand ?? '');
      void attachWeapon('offhand', equipped.offhand ?? '');
      void attachArmor('chest', equipped.chest ?? '');
      void attachArmor('legs', equipped.legs ?? '');
      void attachArmor('feet', equipped.feet ?? '');
      void attachArmor('hands', equipped.hands ?? '');
      void attachArmor('shoulders', equipped.shoulders ?? '');
      void attachArmor('head', equipped.head ?? '');
      void attachArmor('shirt', equipped.shirt ?? '');
      void attachArmor('wrists', equipped.wrists ?? '');
      void attachArmor('waist', equipped.waist ?? '');
      void attachArmor('cape', equipped.cape ?? '');
      void attachArmor('tabard', equipped.tabard ?? '');

      // R232q65: armor texture overlay for the body slots Sirus has NO
      // separate M2 mesh for. The mesh-attach calls above are kept (they
      // place CC0 placeholder meshes on bones); the overlay runs in
      // parallel, painting the correct DBC texture on the body diffuse.
      // Silent fall-through when /dbc/item-texture returns no row OR the
      // BLP-PNG isn't extracted yet — body keeps its skin texture.
      const overlay: Partial<Record<ArmorOverlaySlot, string | null | undefined>> = {
        chest:  equipped.chest,
        legs:   equipped.legs,
        feet:   equipped.feet,
        hands:  equipped.hands,
        wrists: equipped.wrists,
      };
      void applyEquipmentArmorOverlays(scene, { meshes: result.meshes }, overlay)
        .catch(() => { /* silent — overlay never breaks equipment swap */ });
    },
    setCosmetics: () => { /* TODO: cape/pet attach to bone */ },
    triggerAttack: () => {
      const atk = animGroups.find((g) => /attack|swing|strike|hit/i.test(g.name));
      if (atk) {
        const prev = current;
        atk.onAnimationGroupEndObservable.addOnce(() => {
          if (prev) prev.start(true);
          current = prev;
        });
        if (current) current.stop();
        atk.start(false);
        current = atk;
      }
    },
    playEmote: (name: string) => {
      const want = name.toLowerCase();
      const synonyms: Record<string, string[]> = {
        dance:  ['dance', 'tans', 'jig'],
        wave:   ['wave', 'greet', 'hello'],
        sit:    ['sit', 'kneel'],
        bow:    ['bow', 'salute'],
        cheer:  ['cheer', 'celebr', 'yell'],
        salute: ['salute', 'wave'],
        laugh:  ['laugh', 'happy'],
        cry:    ['cry', 'sad'],
        kneel:  ['kneel', 'sit', 'pray'],
        sleep:  ['sleep', 'rest', 'idle'],
        jump:   ['jump', 'leap', 'hop'],
        roll:   ['roll', 'dodge'],
      };
      const candidates = synonyms[want] ?? [want];
      const emote = animGroups.find((g) => {
        const n = g.name.toLowerCase();
        return candidates.some((c) => n.includes(c));
      });
      if (!emote) return;
      const prev = current;
      emote.onAnimationGroupEndObservable.addOnce(() => {
        if (prev && prev !== emote) prev.start(true);
        current = prev;
      });
      if (current) current.stop();
      emote.start(false);
      current = emote;
    },
    setCustomization: async (custom: CharCustomization): Promise<void> => {
      // R232q53: swap textures on the body / face / hair / facial-hair
      // sub-meshes. Race + gender are resolved from the loader options;
      // when neither is known (rare smoke-test code path) we silently
      // no-op rather than guess, since DBC lookups need the race id to
      // pick the right texture row.
      if (!opts.raceId || !opts.gender) return;
      try {
        await applyCustomization(scene, { meshes: result.meshes }, opts.raceId, opts.gender, custom);
      } catch (e) {
        // Customization is purely cosmetic — never break the rig load.
        console.debug('[char appearance] customization swap soft-fail', (e as Error)?.message ?? e);
      }
    },
    triggerAnim: (name: string): boolean => {
      // R232q5b1 (Sirus visuals): cast/spell anim dispatch. Sirus M2→GLB
      // export commonly produces names like "stand", "walk", "run",
      // "spellcast_omni_1h", "channel", "cast", "spellcast", "ready1h".
      // We map a few high-signal aliases up front, then fall back to a
      // permissive substring match.
      const want = name.toLowerCase();
      const aliasMap: Record<string, string[]> = {
        cast:           ['spellcast', 'cast', 'spell', 'channel'],
        castfrost:      ['spellcastomni', 'frost', 'spellcast', 'cast', 'spell'],
        castfire:       ['spellcastomni', 'fire', 'spellcast', 'cast', 'spell'],
        castholy:       ['spellcastomni', 'holy', 'spellcast', 'cast', 'spell'],
        castshadow:     ['spellcastdirected', 'shadow', 'spellcast', 'cast', 'spell'],
        castnature:     ['spellcastomni', 'nature', 'spellcast', 'cast', 'spell'],
        castarcane:     ['spellcastomni', 'arcane', 'spellcast', 'cast', 'spell'],
        channel:        ['channel', 'spellchannel', 'channelomni', 'cast'],
        onehandedswingclose: ['attack1h', 'swing', 'attackcrit', 'attack', 'strike'],
        twohandedswing:      ['attack2h', 'swing', 'attackcrit', 'attack', 'strike'],
        onehandedswing:      ['attack1h', 'swing', 'attackcrit', 'attack', 'strike'],
        ready:          ['readyspell', 'ready', 'idle'],
      };
      const candidates = aliasMap[want] ?? [want];
      // Try in priority order — first alias that matches wins.
      let chosen: AnimationGroup | null = null;
      for (const c of candidates) {
        const n = animGroups.find((g) => g.name.toLowerCase().includes(c));
        if (n) { chosen = n; break; }
      }
      if (!chosen) return false;
      const prev = current;
      chosen.onAnimationGroupEndObservable.addOnce(() => {
        // Restore the prior loop (idle/walk) once the cast anim completes.
        if (prev && prev !== chosen) prev.start(true);
        current = prev;
      });
      if (current) current.stop();
      chosen.start(false);
      current = chosen;
      return true;
    },
    dispose: () => {
      for (const slot of attachedMeshes.keys()) disposeSlot(slot);
      attachedMeshes.clear();
      attachedRoots.clear();
      for (const g of animGroups) g.dispose();
      for (const m of result.meshes) (m as Mesh).dispose(false, true);
      root.dispose(false, true);
    },
  };
}
